import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Gift, Sparkles, X } from "lucide-react";
import toast from "react-hot-toast";
import {
  stampDailyClaimed,
  hasDailyClaimedToday,
} from "../utils/dailyClaimMarker";

/**
 * Auto-triggering daily login reward popup.
 *
 * Loop / logout fix (2026-02-27):
 *  1. All claim surfaces (this popup, RewardsPage, Dashboard, Profile) call
 *     the shared `stampDailyClaimed()` helper on success so the marker is
 *     honoured no matter where the user claims from.
 *  2. The 1.5s "delay before showing" setTimeout is stored in a ref, cleared
 *     on unmount, AND re-checks BOTH the localStorage marker and the fresh
 *     backend `claimed_today` flag immediately before calling setOpen(true).
 *     If either flipped while the timer was pending, we abort silently.
 *  3. The popup is hard-blocked on `/game/*` at the App.js mount site so an
 *     overlay never covers a running game and traps the player into the
 *     10-minute inactivity auto-logout.
 */
export default function DailyLoginPopup() {
  const { token, user, fetchUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);
  // Single-shot guard — once we've decided to show (or skip) the popup for
  // this mount, never re-trigger the network call.
  const _openedRef = useRef(false);
  // Timer for the 1.5s "open after page loads" delay. Stored so we can clear
  // it on unmount and inside the pre-open guard.
  const _openTimerRef = useRef(null);
  // Latest known claimed-today state (from backend). Read by the pre-open
  // guard so a status refetch after this ref updates can abort the pending
  // setOpen.
  const _claimedTodayRef = useRef(false);

  useEffect(() => {
    if (!token || !user) return undefined;
    if (_openedRef.current) return undefined;
    if (hasDailyClaimedToday()) {
      _openedRef.current = true;
      return undefined;
    }
    _openedRef.current = true;
    axios.get(`${API}/bonus/daily/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setStatus(r.data);
        _claimedTodayRef.current = !!r.data?.claimed_today;
        if (r.data?.claimed_today) {
          // Backend already claimed today (e.g. from another device) →
          // stamp the marker so we never bother the user again today.
          stampDailyClaimed();
          return;
        }
        // Delay so the page loads first — then re-check everything before
        // actually opening. If a parallel surface has claimed in the
        // meantime, or the marker got stamped by another tab, abort.
        _openTimerRef.current = setTimeout(() => {
          _openTimerRef.current = null;
          if (hasDailyClaimedToday()) return;
          if (_claimedTodayRef.current) return;
          setOpen(true);
        }, 1500);
      })
      .catch(() => {
        // Never surface a 401/network error to the auth layer — releasing
        // the single-shot guard would just retry the same failing request
        // in a loop. Silently give up until tomorrow.
      });

    return () => {
      if (_openTimerRef.current) {
        clearTimeout(_openTimerRef.current);
        _openTimerRef.current = null;
      }
    };
  }, [token, user?.id]);

  // Listen for "daily bonus claimed elsewhere" broadcast so if the player
  // taps Claim on the Rewards / Dashboard / Profile pages while this popup
  // is mounted, we mirror the state and close silently.
  useEffect(() => {
    const onClaimed = () => {
      _claimedTodayRef.current = true;
      if (_openTimerRef.current) {
        clearTimeout(_openTimerRef.current);
        _openTimerRef.current = null;
      }
      setOpen(false);
    };
    window.addEventListener("daily-bonus:claimed", onClaimed);
    return () => window.removeEventListener("daily-bonus:claimed", onClaimed);
  }, []);

  const claim = async () => {
    setClaiming(true);
    try {
      const r = await axios.post(`${API}/bonus/daily`, {}, { headers: { Authorization: `Bearer ${token}` } });
      // Stamp the marker + close the popup IMMEDIATELY so no other effect
      // re-opens it while we refresh the user balance below.
      stampDailyClaimed();
      _claimedTodayRef.current = true;
      setOpen(false);
      try { window.dispatchEvent(new CustomEvent("daily-bonus:claimed")); } catch (e) { void e; }
      const sc = Number(r.data?.sweeps_coins ?? r.data?.sc_awarded ?? r.data?.bonus_sc ?? 0);
      const gc = Number(r.data?.gold_coins ?? r.data?.gc_awarded ?? r.data?.bonus_gc ?? 0);
      const streak = Number(r.data?.streak || 0);
      toast.success(
        `Daily bonus claimed! +${sc.toFixed(2)} SC · +${gc.toLocaleString()} GC` +
        (streak > 1 ? ` · Day ${streak} streak 🔥` : ""),
        { duration: 6000 }
      );
      // Refresh live user balance in nav. Wrapped so a transient /auth/me
      // failure here can never cascade into an auth-layer logout on the
      // claim happy-path.
      try { await fetchUser(); } catch (e) { void e; }
      window.dispatchEvent(new CustomEvent("sc:bet-settled"));
      window.dispatchEvent(new CustomEvent("balance:refresh"));
    } catch (e) {
      // If the backend says "already claimed" (400), still stamp the marker
      // so we don't nag the user again today.
      if (e.response?.status === 400) {
        stampDailyClaimed();
        _claimedTodayRef.current = true;
        setOpen(false);
      }
      toast.error(e.response?.data?.detail || "Failed to claim");
    } finally { setClaiming(false); }
  };

  const dismiss = () => {
    stampDailyClaimed();
    setOpen(false);
  };

  if (!open || !status) return null;

  const sc = status?.next_sc || status?.base_sc || 0.10;
  const gc = status?.next_gc || status?.base_gc || 100000;
  const tier = user?.vip_rank || "Bronze";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="daily-login-popup">
      <div className="relative w-full max-w-md bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-600 rounded-3xl p-1 shadow-2xl animate-in fade-in zoom-in duration-300">
        <div className="bg-zinc-950 rounded-[22px] p-6 sm:p-8 text-center">
          <button onClick={dismiss} className="absolute top-3 right-3 text-white/70 hover:text-white" data-testid="daily-popup-close">
            <X className="w-5 h-5" />
          </button>
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 animate-bounce">
            <Gift className="w-10 h-10 text-black" />
          </div>
          <h2 className="text-2xl font-black text-white mb-1">Daily Login Reward</h2>
          <p className="text-xs text-zinc-400 mb-6">Come back every day to claim your VIP bonus</p>
          <div className="bg-white/5 border border-amber-500/40 rounded-2xl p-4 mb-6">
            <div className="text-[11px] text-amber-300 uppercase tracking-wider mb-2 flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3" /> {tier} tier reward
            </div>
            <div className="flex items-center justify-around">
              <div>
                <div className="text-3xl font-black text-emerald-300">+{Number(sc).toFixed(2)}</div>
                <div className="text-[10px] text-zinc-500 uppercase">Sweeps Coins</div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="text-3xl font-black text-amber-300">+{Number(gc).toLocaleString()}</div>
                <div className="text-[10px] text-zinc-500 uppercase">Gold Coins</div>
              </div>
            </div>
          </div>
          <Button
            onClick={claim}
            disabled={claiming}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-black text-lg py-6 rounded-2xl"
            data-testid="daily-popup-claim-btn"
          >
            {claiming ? "Claiming..." : "🎁 Claim Now"}
          </Button>
          <button onClick={dismiss} className="text-xs text-zinc-500 hover:text-zinc-300 mt-4">
            Remind me later
          </button>
        </div>
      </div>
    </div>
  );
}
